/**
 * firmaAvanzata.js — la firma grafometrica avanzata (FEA, Namirial) vista
 * dalla pagina: chiede al gestionale di preparare il PDF, lo fa firmare sulla
 * tavoletta dal servizio locale FirmaCerta del PC, e riporta il firmato al
 * gestionale con la sessione dell'utente.
 *
 * SPENTA DI SERIE. La usa solo chi legge `avanzata: true` da
 * GET /template/firma-avanzata/stato, cioe' con FIRMA_CONSENSI_PROVIDER=namirial
 * dichiarato su una macchina di prova (backend/services/firma/provider.js).
 * Con il provider «interno» questo file non fa NIENTE oltre alla lettura dello
 * stato: nessuno script del servizio viene caricato, nessuna chiamata a
 * 127.0.0.1:7777.
 *
 * IL SERVIZIO LOCALE. Dall'installer FirmaCerta_SDK_WEB parte fchttpserver sul
 * PC della firma, porta 7777, SOLO https. La pagina carica
 * https://127.0.0.1:7777/files/fcsign.js, che espone window.fcsign; le
 * chiamate sono richieste <script> in stile JSONP e la risposta arriva a
 * UN SOLO callback globale (fcsign.callback): un'operazione alla volta, con un
 * tempo massimo. Cosi' l'ha vista la pagina di prova sul PC di Alex il 16/10
 * (prova-firma.js v5); la sequenza qui sotto e' la stessa.
 *
 * COSA E' SICURO E COSA E' SUPPOSTO (da riguardare con la Wacom, 19/10):
 *   sicuro   fcsign.js e la forma di window.fcsign; i metodi
 *            getSupportedTablets(soloCollegate), tabletStatusById(vendor,
 *            product), loadDocumentInMemory(url), isDocumentInMemory(),
 *            sign(file, nome, uploadUrl, nomeFirmato, 0),
 *            readSignedDocumentArray() -> { content: [byte] }, clearMemory();
 *            i codici 1000 («File da processare non trovato») e 118
 *            («Certificato per firma grafometrica non trovato»); i parametri
 *            di sign in fcsign.extraParams separati da ';'; dal 19/10 (STU-540)
 *            il codice 16 «Non ci sono firme da effettuare», uguale per il
 *            campo mancante e per quello gia' firmato (controllaFirma), e
 *            readSignedDocumentArray vuoto dopo una firma con OverwriteFile=1
 *            su file.
 *   uploadUrl, il terzo argomento di sign(), e' SEMPRE vuoto: il firmato si legge
 *            dalla memoria del servizio (readSignedDocumentArray) e lo porta al
 *            gestionale la pagina, CON la sessione. Il modo «file» (il servizio
 *            che consegna il firmato a una rotta del gestionale senza sessione)
 *            e' stato tolto per decisione di Alex del 22/10/2026; il ripiego, se
 *            servira' (domanda 17 a Namirial), sara' la strada (b): chi lavora
 *            sceglie il file firmato, la pagina lo legge e lo manda con la sessione.
 *   supposto che il servizio SCARICHI il documento dall'indirizzo del
 *            gestionale (il PDF e' in memoria del backend per cinque minuti,
 *            services/firma/documentoPreparato.js): sul PC il 16/10 ha
 *            firmato un percorso locale, e un indirizzo https dello stesso
 *            servizio ha dato 1000. Rete, certificato e, sul beta, il
 *            cancello di Nginx possono impedirlo;
 *            quale delle due varianti di sign() funziona dopo
 *            loadDocumentInMemory: con lo stesso indirizzo ('indirizzo') o
 *            con l'indirizzo vuoto ('vuoto');
 *            DI SERIE 'vuoto': il codice del PDF vale UNA lettura (decisione di Alex,
 *            18/10) e 'indirizzo' scarica il documento due volte (carico e firma).
 *            Se il servizio scarica comunque piu' volte, si alza FIRMA_PDF_LETTURE
 *            sul server; le righe [firma-documento] del suo registro lo dicono;
 *            il certificato «medium» installato una volta per PC
 *            (installManagedCertificate): la pagina NON lo installa, perche'
 *            la sua password non deve passare dal gestionale. Se manca, 118.
 *
 * LA CONFIGURAZIONE DELLA POSTAZIONE sta in localStorage
 * (rimedichub_firma_sdk, JSON): `base` (di serie https://127.0.0.1:7777; si
 * accetta solo 127.0.0.1 o localhost, http o https), `modoSign` ('vuoto' di
 * serie, o 'indirizzo'). Serve alle prove (il servizio finto sta su un'altra porta) e al
 * 19/10, per provare le due varianti senza ricompilare. E' per postazione e non
 * decide niente di fiscale: i parametri della firma, motivo e luogo li
 * compone il server e li rilegge dal PDF firmato.
 *
 * NEL REFERTO O NEL LOG: mai l'indirizzo del PDF (ha un codice), mai i byte
 * del firmato, mai il nome del paziente. Un errore porta il codice del
 * servizio e una frase fissa.
 */
import { useEffect, useState } from 'react';
import api from './api';

export const SDK_BASE_DI_SERIE = 'https://127.0.0.1:7777';
const CHIAVE_CONFIGURAZIONE = 'rimedichub_firma_sdk';
const ATTESA_MS = { breve: 20000, firma: 300000, svuota: 5000 };

/* ---------------------------------------------------------------- */

/** La configurazione della postazione; ogni valore non valido torna al valore di serie. */
export function configurazione() {
    const c = { base: SDK_BASE_DI_SERIE, modoSign: 'vuoto' };
    let letto = null;
    try { letto = JSON.parse(window.localStorage.getItem(CHIAVE_CONFIGURAZIONE) || 'null'); } catch (e) { letto = null; }
    if (letto && typeof letto === 'object') {
        try {
            const u = new URL(String(letto.base));
            if (['https:', 'http:'].includes(u.protocol) && ['127.0.0.1', 'localhost'].includes(u.hostname)) {
                c.base = `${u.protocol}//${u.host}`;
            }
        } catch (e) { /* resta quella di serie */ }
        if (letto.modoSign === 'vuoto' || letto.modoSign === 'indirizzo') c.modoSign = letto.modoSign;
    }
    return c;
}

/* ---------------------------------------------------------------- */
/* Lo stato: chi firma i consensi, chiesto una volta per pagina.      */

let statoInCorso = null;
let statoNoto = null;

/** { avanzata, provider, configurata }: mai un'eccezione, e un errore di rete non si ricorda. */
export function leggiStatoFirma() {
    if (statoNoto) return Promise.resolve(statoNoto);
    if (!statoInCorso) {
        statoInCorso = api.get('/template/firma-avanzata/stato')
            .then((r) => {
                const d = (r && r.data && r.data.data) || {};
                statoNoto = { avanzata: d.avanzata === true, provider: String(d.provider || 'interno'), configurata: d.configurata !== false };
                return statoNoto;
            })
            .catch(() => ({ avanzata: false, provider: 'interno', configurata: false, errore: true }))
            .finally(() => { statoInCorso = null; });
    }
    return statoInCorso;
}

/** Per le prove: dimentica lo stato noto. */
export function dimenticaStatoFirma() { statoNoto = null; statoInCorso = null; }

/**
 * null finche' non lo si sa; poi { avanzata, ... }. Chi lo usa aspetta di
 * saperlo prima di disegnare la firma: cosi' la schermata non cambia sotto le
 * dita di chi ha gia' cominciato a disegnare.
 */
export function useFirmaAvanzata() {
    const [stato, setStato] = useState(statoNoto);
    useEffect(() => {
        if (statoNoto) { setStato(statoNoto); return undefined; }
        let attivo = true;
        leggiStatoFirma().then((s) => { if (attivo) setStato(s); });
        return () => { attivo = false; };
    }, []);
    return stato;
}

/* ---------------------------------------------------------------- */
/* Il servizio locale                                                 */

export class ErroreFirma extends Error {
    constructor(messaggio, codice) {
        super(messaggio);
        this.name = 'ErroreFirma';
        this.codiceServizio = codice === undefined ? null : codice;
    }
}

let sdkBase = null;

function caricaSdk(base) {
    if (sdkBase === base && typeof window.fcsign !== 'undefined') return Promise.resolve();
    return new Promise((ok, ko) => {
        const s = document.createElement('script');
        s.src = `${base}/files/fcsign.js`;
        s.onload = () => {
            if (typeof window.fcsign === 'undefined') {
                ko(new ErroreFirma('Il servizio di firma di questo computer ha risposto, ma non e\' quello atteso.'));
                return;
            }
            sdkBase = base;
            ok();
        };
        s.onerror = () => {
            s.remove();
            ko(new ErroreFirma('Il servizio di firma non risponde su questo computer. Controlla che FirmaCerta sia acceso, che il browser si fidi del suo certificato (apri una volta il suo indirizzo locale) e che questa pagina sia autorizzata a chiamarlo.'));
        };
        document.head.appendChild(s);
    });
}

/* fcsign ha UN callback globale: un'operazione alla volta. */
let inCorso = false;
function chiama(nome, args, attesa) {
    return new Promise((ok, ko) => {
        if (typeof window.fcsign === 'undefined') { ko(new ErroreFirma('Il servizio di firma non e\' caricato.')); return; }
        if (inCorso) { ko(new ErroreFirma('Un\'altra operazione di firma e\' in corso su questo computer.')); return; }
        inCorso = true;
        const t = setTimeout(() => {
            inCorso = false;
            ko(new ErroreFirma(`Il servizio di firma non ha risposto in ${Math.round(attesa / 1000)} secondi (${nome}).`));
        }, attesa);
        window.fcsign.callback = (dati) => { clearTimeout(t); inCorso = false; ok(dati || {}); };
        try {
            window.fcsign[nome].apply(window.fcsign, args);
        } catch (e) {
            clearTimeout(t); inCorso = false; ko(new ErroreFirma(`Il servizio di firma non accetta la richiesta (${nome}).`));
        }
    });
}

/* Il messaggio del servizio porta a volte un indirizzo (col codice monouso del PDF) o
   un percorso del PC: a schermo non vanno, al loro posto «…». */
const senzaIndirizzi = (t) => String(t)
    .replace(/[a-z][a-z0-9+.-]*:\/\/\S+/gi, '…')
    .replace(/[A-Za-z]:\\\S*/g, '…')
    .replace(/\\\\\S+/g, '…');

/**
 * L'esito di una risposta senza successo del servizio: { esito, frase }. Mai una riuscita.
 *
 * IL CODICE NON BASTA (prova del 19/10 con la STU-540). Lo stesso 1000 e' arrivato per
 * un indirizzo che risponde 404 e per un percorso che non esiste («File da processare non
 * trovato») e per un file che il servizio non e' riuscito a riscrivere con OverwriteFile=1
 * («Non si dispone delle autorizzazioni necessarie per modificare il file ...»); un
 * indirizzo che il servizio non riesce a scaricare (un 403 di Nginx) ha dato «Errore di
 * connessione http» col codice 0. E IL MESSAGGIO NON DICE LA CAUSA: quel «non si dispone
 * delle autorizzazioni» non era un problema di permessi, era un percorso con le barre
 * mescolate (C:\...\Desktop/file.pdf), che il servizio ha trasformato in una cartella che
 * non esiste; con le sole «\» lo stesso file si e' firmato. Qui il messaggio si usa solo
 * come INDIZIO DICHIARATO, per dire a chi lavora che cosa guardare, mai come diagnosi e
 * mai come riuscita; il 1000 con un messaggio che non si riconosce resta «1000 non
 * riconosciuto».
 */
export function esitoDelServizio(r) {
    const codice = r && r.errorCode !== undefined && r.errorCode !== null && r.errorCode !== '' ? Number(r.errorCode) : null;
    const msg = r && r.errorMessage ? String(r.errorMessage).replace(/\s+/g, ' ') : '';
    if (codice === 1000) {
        if (/non trovato/i.test(msg)) {
            return { esito: 'documento_non_trovato', frase: 'Il servizio di firma non ha trovato il documento da firmare (codice 1000).' };
        }
        if (/autorizzazion|modificare il file/i.test(msg)) {
            return { esito: 'file_non_scritto', frase: 'Il servizio di firma non e\' riuscito a scrivere il file firmato su questo computer (codice 1000). Il servizio parla di autorizzazioni, ma la causa puo\' essere un\'altra, per esempio il percorso del file: il consenso non e\' firmato.' };
        }
        return { esito: '1000_non_riconosciuto', frase: 'La firma non e\' riuscita (codice 1000, con un messaggio del servizio che il gestionale non riconosce).' };
    }
    if (codice === 118) {
        return { esito: 'certificato_mancante', frase: 'Su questo computer manca il certificato per la firma grafometrica (codice 118): va installato una volta, con le istruzioni della prova.' };
    }
    if (/errore di connessione http/i.test(msg)) {
        return { esito: 'connessione', frase: `Il servizio di firma non e\' riuscito a scaricare il documento dal gestionale (errore di connessione${codice !== null ? `, codice ${codice}` : ''}).` };
    }
    const breve = senzaIndirizzi(msg).slice(0, 120);
    return { esito: 'non_riuscita', frase: `La firma non e\' riuscita${breve ? `: ${breve}` : ''}${codice !== null ? ` (codice ${codice})` : ''}.` };
}

/** La frase per un esito senza successo del servizio. Mai testo del documento, mai un indirizzo. */
export function fraseDelServizio(r) {
    return esitoDelServizio(r).frase;
}

function controlla(r) {
    if (!r || r.success !== true) {
        const { esito, frase } = esitoDelServizio(r);
        const e = new ErroreFirma(frase, r && r.errorCode);
        e.esito = esito;
        throw e;
    }
    return r;
}

/**
 * L'esito di sign(). Il CODICE 16 («Non ci sono firme da effettuare») non e' MAI
 * una riuscita, nemmeno se arrivasse con success vero: il servizio lo da' sia per
 * il campo mancante sia per il campo gia' firmato (prova del 19/10). Che cosa vuol
 * dire per questo documento lo ha detto il server nella «prepara» (`se_codice_16`,
 * services/firma/campoFirma.js), perche' e' lui che ha guardato il PDF: la pagina
 * non ha una copia della regola. Senza quella risposta resta la frase generica,
 * non riuscita.
 */
export function controllaFirma(r, preparato) {
    if (r && Number(r.errorCode) === 16) {
        const esito = preparato && preparato.se_codice_16;
        const e = new ErroreFirma(esito && esito.messaggio ? String(esito.messaggio) : fraseDelServizio(r), 16);
        e.esito = esito && esito.codice ? String(esito.codice) : 'nessuna_firma_da_fare';
        throw e;
    }
    return controlla(r);
}

const PDF_ALL_INIZIO = (byte) => byte && byte.length >= 8
    && String.fromCharCode(byte[0], byte[1], byte[2], byte[3], byte[4]) === '%PDF-';

/** Fra le tavolette, la STU-540 se c'e', altrimenti la prima. */
function sceltaTavoletta(elenco) {
    const lista = Array.isArray(elenco) ? elenco : [];
    return lista.find((t) => /540/.test(`${t.modelAsString} ${t.name}`)) || lista[0] || null;
}

/** I parametri della firma: quelli del server (ordine) piu' la tavoletta di questo PC. */
export function parametriDellaFirma(extraParams, tavoletta) {
    const lista = [String(extraParams || '')];
    if (tavoletta) {
        if (tavoletta.isTabletPC) lista.push('AdvancedSignature.FromTabletPC=1', `AdvancedSignature.TabletPCModel=${tavoletta.modelAsString}`);
        else lista.push('AdvancedSignature.FromTabletPC=0', `AdvancedSignature.TabletModel=${tavoletta.modelAsString}`);
    }
    return lista.filter(Boolean).join(';');
}

/**
 * Fa firmare il PDF preparato e torna con i byte del firmato.
 *
 * @param {object} preparato  la risposta di POST /template/firma-avanzata/prepara
 *                            (extraParams, pdf_percorso, nome_file)
 * @param {(passo: string) => void} [alPasso]  'tavoletta' | 'documento' | 'firma' | 'lettura'
 * @returns {Promise<Uint8Array>}
 * @throws {ErroreFirma} con una frase per chi lavora
 */
export async function firmaSullaTavoletta(preparato, alPasso = () => {}) {
    const cfg = configurazione();
    await caricaSdk(cfg.base);
    let svuotare = false;
    try {
        alPasso('tavoletta');
        const elenco = controlla(await chiama('getSupportedTablets', [true], ATTESA_MS.breve));
        const tavoletta = sceltaTavoletta(elenco.tablets);
        if (!tavoletta) throw new ErroreFirma('Nessuna tavoletta collegata a questo computer.');
        const stato = await chiama('tabletStatusById', [tavoletta.vendorId, tavoletta.productId], ATTESA_MS.breve);
        if (stato && stato.success === true && stato.successMessage && stato.successMessage !== 'Connected') {
            throw new ErroreFirma(`La tavoletta non risulta collegata (${String(stato.successMessage).slice(0, 40)}).`);
        }

        alPasso('documento');
        const indirizzo = `${window.location.origin}${preparato.pdf_percorso}`;
        window.fcsign.extraParams = parametriDellaFirma(preparato.extraParams, tavoletta);
        svuotare = true;

        controlla(await chiama('loadDocumentInMemory', [indirizzo], ATTESA_MS.breve));
        const inMemoria = await chiama('isDocumentInMemory', [], ATTESA_MS.breve);
        if (!inMemoria || inMemoria.success !== true) {
            throw new ErroreFirma('Il servizio di firma non ha il documento in memoria.');
        }

        alPasso('firma');
        const daFirmare = cfg.modoSign === 'vuoto' ? '' : indirizzo;
        /* uploadUrl SEMPRE vuoto (decisione di Alex del 22/10/2026): il firmato si legge dalla
           memoria (readSignedDocumentArray) e lo porta al gestionale la pagina, con la sessione. */
        controllaFirma(await chiama('sign', [daFirmare, preparato.nome_file || 'consenso.pdf', '', 'consenso-firmato.pdf', 0], ATTESA_MS.firma), preparato);

        alPasso('lettura');
        const letto = controlla(await chiama('readSignedDocumentArray', [], ATTESA_MS.breve));
        if (!Array.isArray(letto.content) || letto.content.length < 8) {
            throw new ErroreFirma('Il servizio di firma non ha restituito il documento firmato.');
        }
        const byte = Uint8Array.from(letto.content);
        if (!PDF_ALL_INIZIO(byte)) {
            throw new ErroreFirma('Il documento restituito dal servizio di firma non e\' un PDF.');
        }
        return byte;
    } finally {
        /* Il documento firmato e i campioni restano nella memoria del servizio,
           che risponde a qualunque pagina aperta sul PC: si svuota sempre, anche
           dopo un errore. Se non risponde, non cambia l'esito della firma. */
        if (svuotare) {
            try { await chiama('clearMemory', [], ATTESA_MS.svuota); } catch (e) { /* la prova dice se e' rimasta piena */ }
        }
    }
}

/* ---------------------------------------------------------------- */
/* Il gestionale                                                      */

/** Chiede al server il PDF col campo di firma vuoto e l'ordine. */
export async function preparaConsensoFea(corpo) {
    const r = await api.post('/template/firma-avanzata/prepara', corpo);
    return r.data.data;
}

/** Porta al server il firmato; il server lo controlla e lo archivia. Torna il documento. */
export async function inviaFirmato(ordine, byte) {
    const fd = new FormData();
    fd.append('ordine', ordine);
    fd.append('firmato', new Blob([byte], { type: 'application/pdf' }), 'firmato.pdf');
    const r = await api.post('/template/firma-avanzata/ricevi', fd, { headers: { 'Content-Type': undefined } });
    return r.data.data.documento;
}

/**
 * Tutto il giro per un consenso: prepara, firma sulla tavoletta, archivia.
 * @param {object} corpo  template_id, paziente_id, visita_id, medico_id, prestazione, risposte
 * @param {(passo: string) => void} [alPasso]  'prepara' | 'tavoletta' | 'documento' | 'firma' | 'lettura' | 'archivia'
 * @returns {Promise<object>} il documento archiviato (la stessa riga della firma a schermo)
 */
export async function firmaConsensoConTavoletta(corpo, alPasso = () => {}) {
    alPasso('prepara');
    const preparato = await preparaConsensoFea(corpo);
    const byte = await firmaSullaTavoletta(preparato, alPasso);
    alPasso('archivia');
    return inviaFirmato(preparato.ordine, byte);
}
